import {
  defaultForMailingListSearchParamsModel,
  MailingListSearchParamsModel,
} from '../../types/requests/MailingListSearchParamsModel';
import {
  defaultMailingListMessageSearchParamsModel,
  MailingListMessageSearchParamsModel,
} from '../../types/requests/MailingListMessageSearchParamsModel';
import {MailingListSearchPaged} from '../../types/views/MailingListSearchPaged';
import {MailingListModel} from '../../types/models/MailingListModel';
import {MailingListMessageSearchPaged} from '../../types/views/MailingListMessageSearchPaged';
import {
  RecipientSearchParamsModel,
} from '../../types/requests/RecipientSearchparamsModel';
import {createReducer, PayloadAction} from '@reduxjs/toolkit';
import {
  MAILING_LIST,
  MAILING_LIST_RECIPIENT,
  MAILING_LIST_MESSAGE_SEARCH,
  MAILING_LIST_RECIPIENT_SEARCH,
  MAILING_LIST_SEARCH,
  MAILING_LIST_MESSAGE,
} from './Actions';
import {ISearchRequest, SearchRequest} from '../../types/common/SearchRequest';
import {SearchView} from '../../types/common/SearchView';
import {MailingListMessageModel} from '../../types/models/MailingListMessageModel';
import {PeopleSearchPaged} from '../../types/views/PeopleSearchPaged';
import {defaultPeopleSearchParamsModel, PeopleSearchParamsModel} from '../../types/requests/PeopleSearchParamsModel';
import {PersonSearchResultPaged} from '../../types/views/PersonSearchResultPaged';

export interface IMailingListState {
  mailingListSearchParams: MailingListSearchParamsModel
  mailingListMessageSearchParams: MailingListMessageSearchParamsModel,
  mailingListSearchSort: string;
  mailingListSearchView: string | undefined;
  mailingListMessageSearchSort: string;
  mailingListResult: MailingListSearchPaged | undefined;
  mailingListResultError: string | undefined;
  mailingListResultLoading: boolean;
  mailingList: MailingListModel | undefined;
  mailingListLoading: boolean;
  mailingListLoadError: string | undefined;
  mailingListSaving: boolean;
  mailingListSaveError: string | undefined;
  mailingListEnabling: boolean;
  mailingListEnableError: string | undefined;
  mailingListDisabling: boolean;
  mailingListDisableError: string | undefined;
  mailingListRecipients: PersonSearchResultPaged | undefined;
  mailingListRecipientSearchParams: PeopleSearchParamsModel | undefined;
  mailingListRecipientsError: string | undefined;
  mailingListRecipientsLoading: boolean;
  mailingListRecipientsRefreshing: boolean;
  mailingListMessages: MailingListMessageSearchPaged | undefined;
  mailingListMessagesLoading: boolean;
  mailingListMessagesRefreshing: boolean;
  mailingListMessagesError: string | undefined;
  mailingListMessage: MailingListMessageModel | undefined;
  mailingListMessageLoading: boolean;
  mailingListMessageRefreshing: boolean;
  mailingListMessageError: string | undefined;
}

const initialState: IMailingListState = {
  mailingListSearchParams: defaultForMailingListSearchParamsModel(),
  mailingListMessageSearchParams: defaultMailingListMessageSearchParamsModel(),
  mailingListSearchSort: 'Created',
  mailingListSearchView: SearchView.Default,
  mailingListResult: undefined,
  mailingListResultError: undefined,
  mailingListResultLoading: false,
  mailingListLoading: false,
  mailingListLoadError: undefined,
  mailingList: undefined,
  mailingListSaving: false,
  mailingListSaveError: undefined,
  mailingListEnabling: false,
  mailingListEnableError: undefined,
  mailingListDisabling: false,
  mailingListDisableError: undefined,
  mailingListRecipients: undefined,
  mailingListRecipientSearchParams: defaultPeopleSearchParamsModel(),
  mailingListRecipientsError: undefined,
  mailingListRecipientsLoading: false,
  mailingListRecipientsRefreshing: false,
  mailingListMessages: undefined,
  mailingListMessageSearchSort: 'Date Added',
  mailingListMessagesLoading: false,
  mailingListMessagesRefreshing: false,
  mailingListMessagesError: undefined,
  mailingListMessage: undefined,
  mailingListMessageLoading: false,
  mailingListMessageRefreshing: false,
  mailingListMessageError: undefined,
};

const mailingListReducer = createReducer(initialState, {
  /* mailing list */
  [MAILING_LIST_SEARCH.CHANGE_VIEW]: (state, action: PayloadAction<string>) => {
    state.mailingListSearchView = action.payload;
  },
  [MAILING_LIST.SAVE]: (state, action: PayloadAction<string>) => {
    state.mailingListSaving = true;
    state.mailingListSaveError = undefined;
  },
  [MAILING_LIST.SAVE_ERROR]: (state, action: PayloadAction<string>) => {
    state.mailingListSaving = false;
    state.mailingListSaveError = action.payload;
  },
  [MAILING_LIST.SAVED]: (state, action: PayloadAction<MailingListModel>) => {
    state.mailingListSaving = false;
    state.mailingListSaveError = undefined;
    state.mailingList = action.payload;
  },
  [MAILING_LIST.LOAD]: (state) => {
    state.mailingListLoading = true;
    state.mailingList = undefined;
    state.mailingListLoadError = undefined;
  },
  [MAILING_LIST.REFRESH]: (state) => {
    state.mailingListLoading = false;
    state.mailingList = undefined;
    state.mailingListLoadError = undefined;
  },
  [MAILING_LIST.LOADED]: (state, action: PayloadAction<MailingListModel>) => {
    state.mailingListLoading = false;
    state.mailingList = action.payload;
    state.mailingListLoadError = undefined;
  },
  [MAILING_LIST.LOAD_ERROR]: (state, action: PayloadAction<string>) => {
    state.mailingListLoading = false;
    state.mailingListLoadError = action.payload;
  },
  [MAILING_LIST.ENABLE]: (state, action: PayloadAction<string>) => {
    state.mailingListEnabling = true;
    state.mailingListEnableError = undefined;
  },
  [MAILING_LIST.ENABLED]: (state) => {
    state.mailingListEnabling = false;
    state.mailingListEnableError = undefined;
  },
  [MAILING_LIST.ENABLE_ERROR]: (state, action: PayloadAction<string>) => {
    state.mailingListEnabling = false;
    state.mailingListEnableError = action.payload;
  },
  [MAILING_LIST.DISABLE]: (state) => {
    state.mailingListDisabling = true;
    state.mailingListDisableError = undefined;
  },
  [MAILING_LIST.DISABLED]: (state) => {
    state.mailingListDisabling = false;
    state.mailingListDisableError = undefined;
  },
  [MAILING_LIST.DISABLE_ERROR]: (state, action: PayloadAction<string>) => {
    state.mailingListDisabling = false;
    state.mailingListDisableError = action.payload;
  },
  /* search */
  [MAILING_LIST_SEARCH.LOAD]: (state, action: PayloadAction<ISearchRequest<MailingListSearchParamsModel>>) => {
    state.mailingListSearchParams = action.payload.searchParams;
    state.mailingListResultLoading = true;
    state.mailingListResultError = undefined;
  },
  [MAILING_LIST_SEARCH.QUICK_SEARCH]: (state) => {
    state.mailingListResultLoading = false;
    state.mailingListResultError = undefined;
  },
  [MAILING_LIST_SEARCH.LOADED]: (state, action: PayloadAction<MailingListSearchPaged>) => {
    state.mailingListResultLoading = false;
    state.mailingListResultError = undefined;
    state.mailingListResult = action.payload;
  },
  [MAILING_LIST_SEARCH.ERROR]: (state, action: PayloadAction<string>) => {
    state.mailingListResultLoading = false;
    state.mailingListResultError = action.payload;
    state.mailingListResult = undefined;
  },
  /* recipients */
  [MAILING_LIST_RECIPIENT_SEARCH.LOAD]: (state, action: PayloadAction<SearchRequest<PeopleSearchParamsModel>>) => {
    state.mailingListRecipientSearchParams = action.payload.searchParams;
    state.mailingListRecipientsError = undefined;
    state.mailingListRecipientsLoading = true;
  },
  [MAILING_LIST_RECIPIENT_SEARCH.REFRESH]: (state, action: PayloadAction<SearchRequest<PeopleSearchParamsModel>>) => {
    state.mailingListRecipientSearchParams = action.payload.searchParams;
    state.mailingListRecipientsError = undefined;
  },
  [MAILING_LIST_RECIPIENT_SEARCH.LOADED]: (state, action: PayloadAction<PersonSearchResultPaged>) => {
    state.mailingListRecipients = action.payload;
    state.mailingListRecipientsLoading = false;
    state.mailingListRecipientsError = undefined;
  },
  [MAILING_LIST_MESSAGE_SEARCH.LOAD]: (state, action: PayloadAction<SearchRequest<RecipientSearchParamsModel>>) => {
    state.mailingListMessageSearchParams = action.payload.searchParams;
    state.mailingListMessagesError = undefined;
    state.mailingListMessagesLoading = true;
  },
  [MAILING_LIST_MESSAGE_SEARCH.REFRESH]: (state, action: PayloadAction<SearchRequest<RecipientSearchParamsModel>>) => {
    state.mailingListMessageSearchParams = action.payload.searchParams;
    state.mailingListMessagesError = undefined;
  },
  [MAILING_LIST_MESSAGE_SEARCH.LOADED]: (state, action: PayloadAction<PersonSearchResultPaged>) => {
    state.mailingListMessages = action.payload;
    state.mailingListMessagesLoading = false;
    state.mailingListMessagesError = undefined;
  },
  [MAILING_LIST_RECIPIENT.LOADED]: (state, action: PayloadAction<string>) => {
    state.mailingListDisabling = false;
    state.mailingListDisableError = action.payload;
  },
  [MAILING_LIST_RECIPIENT.LOAD]: (state, action: PayloadAction<string>) => {
    state.mailingListDisabling = false;
    state.mailingListDisableError = action.payload;
  },
  [MAILING_LIST_MESSAGE.LOAD]: (state) => {
    state.mailingListMessageLoading = true;
    state.mailingListMessage = undefined;
    state.mailingListMessageError = undefined;
  },
  [MAILING_LIST_MESSAGE.LOADED]: (state, action: PayloadAction<MailingListModel>) => {
    state.mailingListMessageLoading = false;
    state.mailingListMessage = action.payload;
    state.mailingListMessageError = undefined;
  },
  [MAILING_LIST_MESSAGE.LOAD_ERROR]: (state, action: PayloadAction<string>) => {
    state.mailingListMessageLoading = false;
    state.mailingListMessageError = action.payload;
  },

});

export default mailingListReducer;